import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

// Icons
import SearchIcon from "@material-ui/icons/Search";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";

const useStyles = makeStyles(theme => ({
  // Container principal
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.type === "dark" ? "#12121a" : "#ffffff",
  },

  // Header com título
  header: {
    padding: theme.spacing(2, 2, 1, 2),
  },

  headerTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.25),
  },

  headerSubtitle: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },

  // Tabs modernos
  tabsContainer: {
    padding: theme.spacing(1, 2),
  },

  tabsRoot: {
    minHeight: "unset",
  },

  tabsWrapper: {
    display: "flex",
    gap: 4,
    padding: 4,
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" 
      ? alpha("#ffffff", 0.05)
      : alpha("#6366F1", 0.06),
  },

  tabButton: {
    flex: 1,
    minHeight: 36,
    minWidth: 0,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: theme.spacing(0.5, 1),
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    opacity: 1,
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      gap: 4,
      whiteSpace: "nowrap",
    },
    "& svg": {
      fontSize: 16,
      marginBottom: "0 !important",
      marginRight: 0,
    },
    "&.Mui-selected": {
      color: "#fff",
      backgroundColor: "#6366F1",
      boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
    },
    "&:hover:not(.Mui-selected)": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },

  tabIndicator: {
    display: "none",
  },

  // Barra de ações
  actionsBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },

  newTicketButton: {
    borderRadius: 10,
    padding: theme.spacing(0.875, 1.5),
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    minWidth: "auto",
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
    "&:hover": {
      background: "linear-gradient(135deg, #5558E3 0%, #7C4FE8 100%)",
      boxShadow: "0 6px 16px rgba(99, 102, 241, 0.4)",
    },
  },

  switchLabel: {
    marginLeft: "auto",
    marginRight: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    "& .MuiSwitch-root": {
      transform: "scale(0.8)",
    },
  },

  // Barra de busca
  searchContainer: {
    padding: theme.spacing(0, 2, 1, 2),
  },

  searchWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" 
      ? alpha("#ffffff", 0.05)
      : alpha("#000000", 0.03),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: "all 0.2s ease",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.type === "dark" 
        ? alpha("#ffffff", 0.08)
        : alpha(theme.palette.primary.main, 0.05),
    },
  },

  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: 18,
  },

  searchInput: {
    flex: 1,
    fontSize: "0.85rem",
    "& input::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },

  clearButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },

  // Queue Select
  queueSelectWrapper: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "0.8rem",
      minWidth: 90,
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
    },
    "& .MuiSelect-icon": {
      right: 8,
    },
  },

  // Tabs internos (Em atendimento / Aguardando)
  internalTabsContainer: {
    padding: theme.spacing(1, 2),
  },

  internalTabsWrapper: {
    display: "flex",
    gap: theme.spacing(1),
  },

  internalTab: {
    flex: 1,
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
  },

  internalTabActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "& $internalTabText": {
      color: theme.palette.primary.main,
    },
  },

  internalTabText: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    transition: "color 0.2s ease",
  },

  badge: {
    "& .MuiBadge-badge": {
      fontSize: "0.6rem",
      height: 16,
      minWidth: 16,
      fontWeight: 700,
    },
  },

  // Filtros avançados
  filtersContainer: {
    padding: theme.spacing(0, 2, 1, 2),
  },

  // Divider
  divider: {
    height: 1,
    backgroundColor: alpha(theme.palette.divider, 0.08),
    margin: theme.spacing(0, 2),
  },

  // Lista de tickets container
  ticketsListContainer: {
    flex: 1,
    overflow: "hidden",
  },
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isSmall = useMediaQuery(theme.breakpoints.down("xs"));

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
  }, []);

  useEffect(() => {
    if (tab === "search" && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchParam("");
    setTab("open");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  return (
    <Paper elevation={0} className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.headerTitle}>
          Conversas
        </Typography>
        <Typography className={classes.headerSubtitle}>
          Gerencie seus atendimentos
        </Typography>
      </Box>

      {/* Tabs principais */}
      <Box className={classes.tabsContainer}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          classes={{
            root: classes.tabsRoot,
            flexContainer: classes.tabsWrapper,
            indicator: classes.tabIndicator,
          }}
        >
          <Tab
            value="open"
            icon={<MoveToInboxIcon />}
            label={isSmall ? "" : "Aberta"}
            classes={{ root: classes.tabButton }}
          />
          <Tab
            value="closed"
            icon={<CheckBoxIcon />}
            label={isSmall ? "" : "Resolvido"}
            classes={{ root: classes.tabButton }}
          />
          <Tab
            value="search"
            icon={<SearchIcon />}
            label={isSmall ? "" : "Busca"}
            classes={{ root: classes.tabButton }}
          />
        </Tabs>
      </Box>

      {/* Barra de ações */}
      {tab !== "search" && (
        <Box className={classes.actionsBar}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.newTicketButton}
            onClick={() => setNewTicketModalOpen(true)}
          >
            Novo
          </Button>

          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <FormControlLabel
                className={classes.switchLabel}
                label="Todos"
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={showAllTickets}
                    onChange={() => setShowAllTickets((prevState) => !prevState)}
                    name="showAllTickets"
                    color="primary"
                  />
                }
              />
            )}
          />
        </Box>
      )}

      {/* Queue Select - linha separada */}
      {tab !== "search" && (
        <Box px={2} pb={1}>
          <Box className={classes.queueSelectWrapper}>
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setSelectedQueueIds(values)}
            />
          </Box>
        </Box>
      )}

      {/* Barra de busca (apenas na tab search) */}
      {tab === "search" && (
        <Box className={classes.searchContainer}>
          <Box className={classes.searchWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
            {searchParam && (
              <IconButton
                size="small"
                className={classes.clearButton}
                onClick={handleClearSearch}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Divider */}
      <Box className={classes.divider} />

      {/* Tab Open - Subtabs */}
      <TabPanel value={tab} name="open" className={classes.ticketsListContainer}>
        <Box className={classes.internalTabsContainer}>
          <Box className={classes.internalTabsWrapper}>
            <Box
              className={`${classes.internalTab} ${tabOpen === "open" ? classes.internalTabActive : ""}`}
              onClick={() => handleChangeTabOpen("open")}
            >
              <Badge
                badgeContent={openCount}
                color="primary"
                className={classes.badge}
              >
                <Typography className={classes.internalTabText}>
                  Atendendo
                </Typography>
              </Badge>
            </Box>
            <Box
              className={`${classes.internalTab} ${tabOpen === "pending" ? classes.internalTabActive : ""}`}
              onClick={() => handleChangeTabOpen("pending")}
            >
              <Badge
                badgeContent={pendingCount}
                color="secondary"
                className={classes.badge}
              >
                <Typography className={classes.internalTabText}>
                  Aguardando
                </Typography>
              </Badge>
            </Box>
          </Box>
        </Box>

        <Paper elevation={0} className={classes.ticketsListContainer}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
          />
        </Paper>
      </TabPanel>

      {/* Tab Closed */}
      <TabPanel value={tab} name="closed" className={classes.ticketsListContainer}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>

      {/* Tab Search */}
      <TabPanel value={tab} name="search" className={classes.ticketsListContainer}>
        <Box className={classes.filtersContainer}>
          <TagsFilter onFiltered={handleSelectedTags} />
          {profile === "admin" && (
            <UsersFilter onFiltered={handleSelectedUsers} />
          )}
        </Box>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          tags={selectedTags}
          users={selectedUsers}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManagerTabs;
